import React, { useState, useEffect, useRef } from 'react';
import { countryToCellMap, getCountryFromCellId } from './countryToCellMapping';
import { normalizeCountryName } from './countryCodeMapping';
import CountryPath from './CountryPath';

/**
 * WorldMap Component
 * Displays an interactive SVG world map where all countries are clickable
 * 
 * Props:
 * - cellOwnersMap: Map of cellId -> faction name
 * - beaconCells: Array of cell IDs with waiting players
 * - onCountryClick: Callback when a country is clicked (receives cellId)
 * - onCountryHover: Callback when a country is hovered (receives cellId, event)
 * - onCountryLeave: Callback when mouse leaves a country
 * - getFactionBackground: Function to get background color for a faction
 * - deploying: Boolean indicating if deployment is in progress
 * - highlightedCells: Array of cell IDs to highlight (for mission system)
 * - fortifiedCells: Array of cell IDs that are fortified (for mission system)
 * - selectedCell: Cell ID that is currently selected (for mission system)
 * - missionTargetCell: Cell ID of the mission target (for mission system)
 */
const WorldMap = ({
  cellOwnersMap = {},
  beaconCells = [],
  onCountryClick,
  onCountryHover,
  onCountryLeave,
  getFactionBackground,
  deploying = false,
  highlightedCells = [],
  fortifiedCells = [],
  opponentFortifiedCells = [],
  selectedCell = null,
  missionTargetCell = null
}) => {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [hoveredCellId, setHoveredCellId] = useState(null);
  const [svgPaths, setSvgPaths] = useState([]);
  const [svgViewBox, setSvgViewBox] = useState('0 0 1000 500');
  
  // Zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [hasDragged, setHasDragged] = useState(false);
  const [dragStart, setDragStart] = useState(null);
  const [panStart, setPanStart] = useState(null);

  // Load and parse world map SVG
  useEffect(() => {
    const loadWorldMap = async () => {
      try {
        const response = await fetch('/images/world-map.svg');
        if (response.ok) {
          const svgText = await response.text();
          const parser = new DOMParser();
          const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');
          
          // Get viewBox from SVG
          const svgElement = svgDoc.documentElement;
          const viewBox = svgElement.getAttribute('viewBox') || svgElement.getAttribute('viewbox') || '0 0 1000 500';
          setSvgViewBox(viewBox);
          
          // Extract all paths, polygons, and circles
          const paths = svgDoc.querySelectorAll('path, polygon, circle');
          const pathData = Array.from(paths).map((path, index) => {
            // Try multiple ways to get country identifier
            // Priority: name attribute (full country name) > id (country code) > class > title
            const identifier = path.getAttribute('name') || 
                              path.id || 
                              path.getAttribute('class') ||
                              path.getAttribute('title') || 
                              path.getAttribute('data-name') ||
                              path.getAttribute('data-country') ||
                              '';
            
            // Store original identifier for debugging
            const originalIdentifier = identifier;
            
            // Normalize to full country name (handles codes, classes, etc.)
            const countryName = normalizeCountryName(identifier);
            
            const cellId = countryName ? countryToCellMap[countryName] : null;
            
            return {
              id: path.id || `path-${index}`,
              type: path.tagName.toLowerCase(),
              d: path.getAttribute('d') || '',
              points: path.getAttribute('points') || '',
              cx: path.getAttribute('cx') || '',
              cy: path.getAttribute('cy') || '',
              r: path.getAttribute('r') || '',
              countryName,
              cellId,
              originalIdentifier, // Store for debugging
              originalFill: path.getAttribute('fill') || 'rgba(0, 0, 0, 0.1)',
              originalStroke: path.getAttribute('stroke') || 'rgba(255, 255, 255, 0.1)',
              originalStrokeWidth: path.getAttribute('stroke-width') || '1'
            };
          });
          
          setSvgPaths(pathData);
          console.log(`✅ Loaded ${pathData.length} paths from world map`);
          const mappedCount = pathData.filter(p => p.cellId).length;
          console.log(`✅ ${mappedCount} countries mapped to cell IDs`);
          
          // Debug: Show ALL unmapped countries with their exact SVG identifiers
          const unmappedCountries = pathData.filter(p => !p.cellId && p.countryName);
          
          if (unmappedCountries.length > 0) {
            // Only log summary - full list is too verbose
            console.log(`⚠️ ${unmappedCountries.length} unmapped countries (mostly islands/territories). These appear gray on the map.`);
          } else {
            console.log('✅ All countries are mapped!');
          }
          
          // Removed verbose debug logs for specific countries
        } else {
          console.warn('⚠️ World map SVG not found at /images/world-map.svg');
          console.warn('Please add a world-map.svg file with country paths');
        }
      } catch (error) {
        console.error('Error loading world map:', error);
      }
    };

    loadWorldMap();
  }, []);

  const handleCountryClick = React.useCallback((cellId, e) => {
    // Prevent map dragging if clicking on a country
    if (e) {
      e.stopPropagation();
    }
    // Only trigger click if we didn't drag
    if (cellId && onCountryClick && !deploying && !hasDragged) {
      onCountryClick(cellId);
    }
  }, [onCountryClick, deploying, hasDragged]);

  const handleCountryMouseEnter = React.useCallback((e, cellId) => {
    if (cellId && !deploying) {
      setHoveredCellId(cellId);
      if (onCountryHover) {
        onCountryHover(cellId, e);
      }
    }
  }, [onCountryHover, deploying]);

  const handleCountryMouseLeave = React.useCallback(() => {
    setHoveredCellId(null);
    if (onCountryLeave) {
      onCountryLeave();
    }
  }, [onCountryLeave]);

  // Handle zoom with mouse wheel
  const handleWheel = React.useCallback((e) => {
    e.preventDefault();
    if (!containerRef.current) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    
    // Calculate zoom factor (zoom in on scroll down, zoom out on scroll up)
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prevZoom => {
      const newZoom = Math.max(0.5, Math.min(5, prevZoom * zoomFactor));
      
      // Calculate the point in SVG coordinates before zoom
      const svgX = (mouseX - pan.x) / prevZoom;
      const svgY = (mouseY - pan.y) / prevZoom;
      
      // Adjust pan to zoom toward mouse position
      const newPanX = mouseX - svgX * newZoom;
      const newPanY = mouseY - svgY * newZoom;
      
      setPan({ x: newPanX, y: newPanY });
      return newZoom;
    });
  }, [pan]);
  
  // Register wheel event listener with non-passive option to allow preventDefault
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    
    container.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [handleWheel]);

  // Handle mouse down for dragging
  const handleMouseDown = (e) => {
    const isCountry = e.target.tagName === 'path' || e.target.tagName === 'polygon' || e.target.tagName === 'circle';
    
    // Start dragging for:
    // - Middle mouse button (button 1) - works everywhere including countries
    // - Right mouse button (button 2) - works everywhere including countries
    // - Left mouse with Ctrl/Cmd/Shift key - works everywhere including countries
    // - Left mouse on empty space (svg or g element)
    // - Left mouse on countries ONLY if moved enough (handled in mousemove)
    if (e.button === 1 || e.button === 2 || e.ctrlKey || e.metaKey || e.shiftKey || 
        e.target.tagName === 'svg' || e.target.tagName === 'g') {
      if (e.button === 1 || e.button === 2) {
        e.preventDefault();
      }
      setIsDragging(true);
      setHasDragged(false);
      setDragStart({ x: e.clientX, y: e.clientY });
      setPanStart({ x: pan.x, y: pan.y });
    } else if (isCountry && e.button === 0) {
      // Left click on country - prepare for potential drag but don't start dragging yet
      // Only start dragging if mouse moves more than threshold
      setDragStart({ x: e.clientX, y: e.clientY });
      setPanStart({ x: pan.x, y: pan.y });
      // Don't set isDragging yet - let the click work normally
      return; // Don't prevent default - allow click to work
    }
  };

  // Handle mouse move for dragging
  const handleMouseMove = (e) => {
    if (isDragging) {
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      // Mark as dragged if moved more than 3 pixels
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        setHasDragged(true);
      }
      setPan({
        x: panStart.x + deltaX,
        y: panStart.y + deltaY
      });
    } else if (dragStart !== null && panStart !== null) {
      // Check if we should start dragging from a country click
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      // If moved more than 3 pixels, start dragging
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        setIsDragging(true);
        setHasDragged(true);
        setPan({
          x: panStart.x + deltaX,
          y: panStart.y + deltaY
        });
      }
    }
  };

  // Handle mouse up to stop dragging
  const handleMouseUp = () => {
    setIsDragging(false);
    setHasDragged(false);
    // Reset drag start position
    setDragStart(null);
    setPanStart(null);
  };

  // Handle double click to reset zoom and pan
  const handleDoubleClick = (e) => {
    // Don't reset if clicking on a country
    if (e.target.tagName === 'path' || e.target.tagName === 'polygon' || e.target.tagName === 'circle') {
      return;
    }
    e.preventDefault();
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Prevent context menu on right click
  const handleContextMenu = (e) => {
    e.preventDefault();
  };

  // Helper function to convert hex/rgba to RGB values
  const parseColor = (color) => {
    if (!color) return { r: 128, g: 128, b: 128 };
    
    // Handle rgba format
    if (color.startsWith('rgba')) {
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (match) {
        return { r: parseInt(match[1]), g: parseInt(match[2]), b: parseInt(match[3]) };
      }
    }
    
    // Handle hex format
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { r, g, b };
    }
    
    return { r: 128, g: 128, b: 128 };
  };

  // Helper function to lighten/darken a color
  const adjustColor = (color, factor) => {
    const { r, g, b } = parseColor(color);
    const newR = Math.max(0, Math.min(255, Math.round(r * factor)));
    const newG = Math.max(0, Math.min(255, Math.round(g * factor)));
    const newB = Math.max(0, Math.min(255, Math.round(b * factor)));
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  // Helper function to get a darker variant of the faction color for stripes
  const getDarkerStripeColor = (baseColor) => {
    const { r, g, b } = parseColor(baseColor);
    // Create a darker version (reduce brightness by 30-40%)
    const factor = 0.65; // 35% darker
    const newR = Math.max(0, Math.min(255, Math.round(r * factor)));
    const newG = Math.max(0, Math.min(255, Math.round(g * factor)));
    const newB = Math.max(0, Math.min(255, Math.round(b * factor)));
    return `rgb(${newR}, ${newG}, ${newB})`;
  };

  // Generate stripe pattern ID for a fortified country (per faction, not per cell)
  const getStripePatternId = (cellId, faction) => {
    // Use faction only to ensure one pattern per faction
    // Sanitize faction name for SVG ID (remove spaces and special chars)
    const sanitized = (faction || 'default').replace(/[^a-zA-Z0-9]/g, '-');
    return `stripe-pattern-${sanitized}`;
  };

  // Memoize renderPath to ensure it updates when cellOwnersMap changes
  const renderPath = React.useCallback((pathData) => {
    const { cellId, countryName, type, id } = pathData;
    
    // Render all countries - mapped ones are clickable, unmapped ones are gray
    const isMapped = !!cellId;
    
    // Convert cellId to string for consistent lookup (JavaScript object keys are strings)
    const cellIdKey = cellId ? String(cellId) : null;
    const ownerFaction = isMapped ? (cellOwnersMap[cellIdKey] || null) : null;
    
    
    // Calculate background color
    let background;
    if (!isMapped) {
      background = '#808080'; // Gray for unmapped countries
    } else if (!ownerFaction) {
      background = 'rgba(0, 0, 0, 0.1)'; // Very dark/transparent for unclaimed
    } else if (!getFactionBackground) {
      background = 'rgba(0, 0, 0, 0.1)'; // Fallback if function not provided
    } else {
      background = getFactionBackground(ownerFaction);
    }
    
    // Convert cellId to number for beaconCells array comparison (beaconCells contains numbers)
    const hasBeacon = isMapped && beaconCells.includes(Number(cellId));
    const isHovered = isMapped && hoveredCellId === cellId;
    
    // Mission-specific indicators
    const isHighlighted = isMapped && highlightedCells.includes(Number(cellId));
    const isFortified = isMapped && fortifiedCells.includes(Number(cellId));
    const isOpponentFortified = isMapped && opponentFortifiedCells.includes(Number(cellId));
    const isSelected = isMapped && selectedCell === cellId;
    const isMissionTarget = isMapped && missionTargetCell === cellId;
    
    // For fortified countries, use stripe pattern instead of solid fill
    let fillValue = background;
    let stripePatternId = null;
    if (isFortified && ownerFaction && getFactionBackground) {
      stripePatternId = getStripePatternId(cellId, ownerFaction);
      fillValue = `url(#${stripePatternId})`;
    }
    
    // Determine stroke color based on mission indicators
    let strokeColor = 'rgba(255, 255, 255, 0.1)';
    let strokeWidth = '1';
    
    if (isMissionTarget) {
      strokeColor = 'rgba(255, 215, 0, 0.9)'; // Gold for mission target
      strokeWidth = '3';
    } else if (isFortified) {
      // No border for fortified countries - stripe pattern is the main indicator
      strokeColor = 'rgba(255, 255, 255, 0.1)';
      strokeWidth = '1';
    } else if (isOpponentFortified) {
      strokeColor = 'rgba(0, 150, 255, 0.8)'; // Blue for opponent's fortified countries (attackable)
      strokeWidth = '2.5';
    } else if (isSelected) {
      strokeColor = 'rgba(74, 158, 255, 0.9)'; // Blue for selected
      strokeWidth = '2.5';
    } else if (isHighlighted) {
      strokeColor = 'rgba(74, 158, 255, 0.6)'; // Light blue for highlighted
      strokeWidth = '2';
    } else if (isHovered) {
      strokeColor = 'rgba(255, 255, 255, 0.8)';
      strokeWidth = '2';
    } else if (!isMapped) {
      strokeColor = 'rgba(100, 100, 100, 0.1)';
      strokeWidth = '0.5';
    }
    
    const commonProps = {
      id,
      fill: fillValue,
      stroke: strokeColor,
      strokeWidth: strokeWidth,
      opacity: deploying ? 0.5 : 1,
      style: {
        fill: fillValue,
        cursor: isMapped && !deploying ? (isDragging ? 'grabbing' : 'pointer') : 'not-allowed',
        transition: isDragging ? 'none' : 'all 0.2s ease',
        filter: isHovered ? 'brightness(1.3)' : (isFortified ? 'brightness(1.1)' : (isMissionTarget ? 'brightness(1.4)' : 'none')),
        pointerEvents: hasDragged ? 'none' : 'auto'
      },
      onClick: isMapped ? (e) => handleCountryClick(cellId, e) : undefined,
      onMouseEnter: isMapped ? (e) => handleCountryMouseEnter(e, cellId) : undefined,
      onMouseLeave: isMapped ? handleCountryMouseLeave : undefined,
      'data-cell-id': cellId || null,
      'data-country-name': countryName,
      'data-mapped': isMapped ? 'true' : 'false',
      'data-fortified': isFortified ? 'true' : 'false',
      'data-mission-target': isMissionTarget ? 'true' : 'false'
    };
    
    // Use CountryPath component for proper beacon positioning
    // Include ownerFaction in key to force re-render when ownership changes
    return (
      <CountryPath
        key={`${id}-${ownerFaction || 'unclaimed'}`}
        pathData={pathData}
        commonProps={commonProps}
        hasBeacon={hasBeacon}
        type={type}
      />
    );
  }, [cellOwnersMap, getFactionBackground, beaconCells, hoveredCellId, deploying, isDragging, hasDragged, handleCountryClick, handleCountryMouseEnter, handleCountryMouseLeave, highlightedCells, fortifiedCells, selectedCell, missionTargetCell, opponentFortifiedCells]);

  // Generate stripe patterns for all fortified countries
  const stripePatterns = React.useMemo(() => {
    if (!getFactionBackground || !fortifiedCells || fortifiedCells.length === 0) {
      console.log('⚠️ No stripe patterns: getFactionBackground=', !!getFactionBackground, 'fortifiedCells=', fortifiedCells);
      return [];
    }

    const patterns = [];
    const patternIds = new Set(); // Track unique pattern IDs

    // Create one pattern per unique faction
    const processedFactions = new Set();
    
    fortifiedCells.forEach(cellId => {
      const cellIdKey = String(cellId);
      const ownerFaction = cellOwnersMap[cellIdKey];
      
      if (ownerFaction && !processedFactions.has(ownerFaction)) {
        processedFactions.add(ownerFaction);
        
        const patternId = getStripePatternId(cellId, ownerFaction);
        
        const baseColorRgba = getFactionBackground(ownerFaction);
        // Convert rgba to solid rgb for SVG pattern (patterns work better with solid colors)
        const { r, g, b } = parseColor(baseColorRgba);
        const baseColor = `rgb(${r}, ${g}, ${b})`;
        
        // Create stripe pattern with original faction color and darker variant
        const darkerColor = getDarkerStripeColor(baseColor);
        
        patterns.push(
          <pattern
            key={patternId}
            id={patternId}
            patternUnits="userSpaceOnUse"
            width="12"
            height="12"
            patternTransform="rotate(45)"
          >
            {/* Original faction color stripe */}
            <rect width="6" height="12" fill={baseColor} />
            {/* Darker variant stripe */}
            <rect x="6" width="6" height="12" fill={darkerColor} />
          </pattern>
        );
      }
    });

    return patterns;
  }, [fortifiedCells, cellOwnersMap, getFactionBackground]);

  if (svgPaths.length === 0) {
    return (
      <div 
        className="world-map-container"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <div>
          <p style={{ fontSize: '18px', marginBottom: '10px' }}>World Map Loading...</p>
          <p style={{ fontSize: '12px', color: '#aaa', marginBottom: '5px' }}>
            To use an interactive world map:
          </p>
          <p style={{ fontSize: '11px', color: '#888' }}>
            1. Place a world-map.svg file in /public/images/
          </p>
          <p style={{ fontSize: '11px', color: '#888' }}>
            2. Ensure country paths have id attributes matching country names
          </p>
          <p style={{ fontSize: '11px', color: '#888' }}>
            3. Supported formats: path, polygon, circle elements
          </p>
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      className="world-map-container"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1,
        pointerEvents: 'all',
        cursor: isDragging ? 'grabbing' : 'grab',
        overflow: 'hidden'
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
    >
      <svg
        ref={svgRef}
        viewBox={svgViewBox}
        preserveAspectRatio="xMidYMid meet"
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: 'transparent'
        }}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {stripePatterns}
        </defs>
        <g
          transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}
          style={{ transformOrigin: '0 0', pointerEvents: hasDragged ? 'none' : 'auto' }}
        >
          {svgPaths.map(renderPath)}
        </g>
      </svg>
    </div>
  );
};

export default WorldMap;
